import { useEffect } from "react";
import "./app.css";
import "./common.css";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./redux/store";
import AppRoutes from "./routes/AppRoutes";
import ToastNotifications from "./componants/toast/ToastNotifications";
import accountSetupSteps from "./routes/accountSetupSteps";

// A returning session (closed tab, reload) whose profile setup was left
// incomplete (backend's step_completed: false) gets funneled back to
// whichever step they were last on, instead of landing wherever they
// happened to navigate/refresh — the step pages themselves record
// profileSetup.lastStep on mount as the user moves through them.
const SETUP_STEP_PATHS = accountSetupSteps.map((step) => step.path);

const ProfileSetupResumeGuard = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const stepCompleted = useSelector((state) => state.auth.user?.step_completed);
  const lastStep = useSelector((state) => state.profileSetup.lastStep);

  useEffect(() => {
    if (!isLoggedIn || stepCompleted !== false) return;
    if (SETUP_STEP_PATHS.includes(pathname)) return;
    navigate(lastStep || "/personal-information", { replace: true });
  }, [isLoggedIn, stepCompleted, lastStep, pathname, navigate]);

  return null;
};

// Re-applies a previously-picked (persisted) language to the Google
// Translate widget on a fresh load/reload. Polls instead of reusing
// changeGoogleTranslateLanguage's reload-fallback — this runs once at
// app mount, so a reload fallback here could loop if the widget script
// is ever slow to load.
const GoogleTranslateRestore = () => {
  const language = useSelector((state) => state.locale.language);

  useEffect(() => {
    if (!language || language === "en") return undefined;
    let attempts = 0;
    let timer;
    const tryApply = () => {
      const select = document.querySelector(".goog-te-combo");
      if (select) {
        select.value = language;
        select.dispatchEvent(new Event("change"));
        return;
      }
      attempts += 1;
      if (attempts < 20) timer = setTimeout(tryApply, 300);
    };
    timer = setTimeout(tryApply, 500);
    return () => clearTimeout(timer);
    // Restore the persisted choice once on mount only — header.jsx already
    // drives live dropdown changes directly, this effect isn't for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

// React Router doesn't reset scroll position on navigation by itself —
// without this, a new page renders wherever the previous page had been
// scrolled to (looks like the page is sliding in "from below" instead of
// starting at the top).
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <GoogleTranslateRestore />
        <BrowserRouter>
          <ScrollToTop />
          <ProfileSetupResumeGuard />
          <AppRoutes />
        </BrowserRouter>
        <ToastNotifications />
      </PersistGate>
    </Provider>
  );
}

export default App;
